import { useEffect, useMemo, useState } from "react";
import { Badge, Button, Group, NumberInput, PasswordInput, Stack, Table, Text, TextInput } from "@mantine/core";
import { useTranslation } from "react-i18next";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";

const CHANNELS = ["airbnb", "booking", "vrbo"];

function extractErrorMessage(err, fallback) {
  const raw = err?.response?.data?.message ?? err?.message ?? fallback;
  if (typeof raw === "string") return raw;
  if (raw && typeof raw === "object") return raw.title || raw.details || JSON.stringify(raw);
  return fallback;
}

export default function ChannelConnections() {
  const axiosPrivate = useAxiosPrivate();
  const { t } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [properties, setProperties] = useState([]);
  const [selectedPropertyId, setSelectedPropertyId] = useState(null);
  const [channexProperty, setChannexProperty] = useState(null);
  const [channels, setChannels] = useState([]);
  const [actingChannel, setActingChannel] = useState(null);
  const [bookingCode, setBookingCode] = useState("");
  const [showBookingForm, setShowBookingForm] = useState(false);
  const [vrboUsername, setVrboUsername] = useState("");
  const [vrboPassword, setVrboPassword] = useState("");
  const [showVrboForm, setShowVrboForm] = useState(false);
  const [instructions, setInstructions] = useState("");
  const [seasonAdjustments, setSeasonAdjustments] = useState({ base: 0, high: 0, low: 0 });
  const [savingPricing, setSavingPricing] = useState(false);

  const loadProperties = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await axiosPrivate.get("/properties");
      const list = Array.isArray(res?.data?.properties) ? res.data.properties : [];
      setProperties(list);
      setSelectedPropertyId((current) => current || list[0]?.id || null);
    } catch (err) {
      setError(extractErrorMessage(err, t("channel_connections.load_error")));
    } finally {
      setLoading(false);
    }
  };

  const loadChannelStatuses = async (propertyId) => {
    if (!propertyId) return;
    try {
      const res = await axiosPrivate.get(`/channex/properties/${propertyId}/channels`);
      setChannexProperty(res?.data?.channexProperty || null);
      setChannels(Array.isArray(res?.data?.channels) ? res.data.channels : []);
    } catch (err) {
      setError(extractErrorMessage(err, t("channel_connections.load_error")));
    }
  };

  useEffect(() => {
    loadProperties();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedPropertyId) loadChannelStatuses(selectedPropertyId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPropertyId]);

  useEffect(() => {
    const bookingConnection = channels.find((c) => c.channel === "booking");
    const saved = bookingConnection?.seasonPriceAdjustments;
    setSeasonAdjustments({
      base: saved?.base ?? 0,
      high: saved?.high ?? 0,
      low: saved?.low ?? 0,
    });
  }, [channels]);

  const enableChannex = async () => {
    if (!selectedPropertyId) return;
    setActingChannel("enable");
    setError("");
    try {
      await axiosPrivate.post(`/channex/properties/${selectedPropertyId}/enable`);
      await loadChannelStatuses(selectedPropertyId);
    } catch (err) {
      setError(extractErrorMessage(err, t("channel_connections.enable_error")));
    } finally {
      setActingChannel(null);
    }
  };

  const triggerFullSync = async () => {
    if (!selectedPropertyId) return;
    setActingChannel("full-sync");
    setError("");
    try {
      await axiosPrivate.post(`/channex/properties/${selectedPropertyId}/full-sync`);
    } catch (err) {
      setError(extractErrorMessage(err, t("channel_connections.full_sync_error")));
    } finally {
      setActingChannel(null);
    }
  };

  const connectChannel = async (channel) => {
    if (!selectedPropertyId) return;
    if (channel === "booking" && !showBookingForm) {
      setShowBookingForm(true);
      return;
    }
    if (channel === "booking" && !bookingCode.trim()) return;
    if (channel === "vrbo" && !showVrboForm) {
      setShowVrboForm(true);
      return;
    }
    if (channel === "vrbo" && (!vrboUsername.trim() || !vrboPassword.trim())) return;

    let body;
    if (channel === "booking") body = { hotelId: bookingCode.trim() };
    if (channel === "vrbo") body = { username: vrboUsername.trim(), password: vrboPassword };

    setActingChannel(channel);
    setError("");
    setInstructions("");
    try {
      const res = await axiosPrivate.post(
        `/channex/properties/${selectedPropertyId}/channels/${channel}/connect`,
        body
      );
      if (res?.data?.instructions) setInstructions(res.data.instructions);
      if (channel === "booking") setShowBookingForm(false);
      if (channel === "vrbo") {
        setShowVrboForm(false);
        setVrboUsername("");
        setVrboPassword("");
      }
      await loadChannelStatuses(selectedPropertyId);
    } catch (err) {
      setError(extractErrorMessage(err, t("channel_connections.connect_error")));
    } finally {
      setActingChannel(null);
    }
  };

  const saveBookingPricing = async () => {
    if (!selectedPropertyId) return;
    setSavingPricing(true);
    setError("");
    setInstructions("");
    try {
      const res = await axiosPrivate.patch(
        `/channex/properties/${selectedPropertyId}/channels/booking/pricing`,
        {
          base: Number(seasonAdjustments.base) || 0,
          high: Number(seasonAdjustments.high) || 0,
          low: Number(seasonAdjustments.low) || 0,
        }
      );
      if (res?.data?.instructions) setInstructions(res.data.instructions);
      await loadChannelStatuses(selectedPropertyId);
    } catch (err) {
      setError(extractErrorMessage(err, t("channel_connections.price_adjustment_error")));
    } finally {
      setSavingPricing(false);
    }
  };

  const statusByChannel = useMemo(() => {
    const map = {};
    for (const c of channels) map[c.channel] = c;
    return map;
  }, [channels]);

  const isEnabled = channexProperty?.status === "active";

  if (loading) return <Text>{t("channel_connections.loading")}</Text>;

  return (
    <div style={{ padding: 20 }}>
      <Group position="apart" mb="md">
        <Text size="xl" weight={700}>
          {t("channel_connections.title")}
        </Text>
        <Button variant="outline" onClick={() => loadChannelStatuses(selectedPropertyId)}>
          {t("channel_connections.refresh")}
        </Button>
      </Group>

      {error && (
        <Text color="red" mb="md">
          {error}
        </Text>
      )}

      <Group mb="md">
        <select
          value={selectedPropertyId || ""}
          onChange={(e) => setSelectedPropertyId(Number(e.target.value))}
        >
          {properties.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name || p.domain}
            </option>
          ))}
        </select>
      </Group>

      {!isEnabled ? (
        <Button loading={actingChannel === "enable"} onClick={enableChannex}>
          {t("channel_connections.enable")}
        </Button>
      ) : (
        <Stack>
          <Group>
            <Button
              variant="outline"
              loading={actingChannel === "full-sync"}
              onClick={triggerFullSync}
            >
              {t("channel_connections.full_sync")}
            </Button>
          </Group>

          <Table striped highlightOnHover withBorder withColumnBorders>
            <thead>
              <tr>
                <th>{t("channel_connections.channel")}</th>
                <th>{t("channel_connections.status")}</th>
                <th>{t("channel_connections.actions")}</th>
              </tr>
            </thead>
            <tbody>
              {CHANNELS.map((channel) => {
                const connection = statusByChannel[channel];
                const status = connection?.status || "not_connected";
                const color =
                  status === "connected" ? "green" : status === "error" ? "red" : "yellow";

                return (
                  <tr key={channel}>
                    <td>{t(`channel_connections.channels.${channel}`)}</td>
                    <td>
                      <Badge color={color} variant="filled">
                        {status}
                      </Badge>
                    </td>
                    <td>
                      <Button
                        size="xs"
                        loading={actingChannel === channel}
                        disabled={
                          (channel === "booking" && showBookingForm && !bookingCode.trim()) ||
                          (channel === "vrbo" && showVrboForm && (!vrboUsername.trim() || !vrboPassword.trim()))
                        }
                        onClick={() => connectChannel(channel)}
                      >
                        {channel === "booking" && showBookingForm
                          ? t("channel_connections.submit_code")
                          : channel === "vrbo" && showVrboForm
                          ? t("channel_connections.submit_credentials")
                          : status === "connected"
                          ? t("channel_connections.reconnect")
                          : t("channel_connections.connect")}
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>

          {showBookingForm && (
            <TextInput
              label={t("channel_connections.booking_code_label")}
              value={bookingCode}
              onChange={(e) => setBookingCode(e.currentTarget.value)}
            />
          )}

          {showVrboForm && (
            <Stack spacing="xs" maw={300}>
              <TextInput
                label={t("channel_connections.vrbo_username_label")}
                value={vrboUsername}
                onChange={(e) => setVrboUsername(e.currentTarget.value)}
              />
              <PasswordInput
                label={t("channel_connections.vrbo_password_label")}
                value={vrboPassword}
                onChange={(e) => setVrboPassword(e.currentTarget.value)}
              />
            </Stack>
          )}

          {statusByChannel.booking && (
            <Stack spacing="xs">
              <Text weight={600}>{t("channel_connections.booking_pricing_title")}</Text>
              <Group>
                <NumberInput
                  label={t("channel_connections.season_base")}
                  size="xs"
                  w={110}
                  value={seasonAdjustments.base}
                  onChange={(val) =>
                    setSeasonAdjustments((prev) => ({ ...prev, base: val }))
                  }
                  suffix="%"
                  step={1}
                />
                <NumberInput
                  label={t("channel_connections.season_high")}
                  size="xs"
                  w={110}
                  value={seasonAdjustments.high}
                  onChange={(val) =>
                    setSeasonAdjustments((prev) => ({ ...prev, high: val }))
                  }
                  suffix="%"
                  step={1}
                />
                <NumberInput
                  label={t("channel_connections.season_low")}
                  size="xs"
                  w={110}
                  value={seasonAdjustments.low}
                  onChange={(val) =>
                    setSeasonAdjustments((prev) => ({ ...prev, low: val }))
                  }
                  suffix="%"
                  step={1}
                />
                <Button
                  size="xs"
                  variant="outline"
                  loading={savingPricing}
                  onClick={saveBookingPricing}
                  mt={22}
                >
                  {t("channel_connections.save")}
                </Button>
              </Group>
            </Stack>
          )}

          {instructions && <Text size="sm">{instructions}</Text>}
        </Stack>
      )}
    </div>
  );
}
